import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  ClipboardList,
  ArrowLeft,
  ExternalLink,
  FileText,
  Check,
  X,
  Loader2,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  getAppraisalSubmissions,
  approveAppraisalSubmission,
  rejectAppraisalSubmission,
} from "../../../api/agent";
import { DOC_CANVAS, DOC_PAGE } from "../../../components/ui/documentCanvas";

// Dedicated review queue for the hidden "Block of Units" vendor intake form
// (appraisal-intake.html in the Buyer Portal). Submitting the form no longer
// writes to the CRM — it lands here as a PENDING submission. Approving creates
// the Contact + Property; rejecting discards it. Each row carries the full raw
// answers so the detail pane renders without a second request.

const fmtMoney = (v) =>
  v == null || v === ""
    ? "—"
    : "$" + Number(v).toLocaleString("en-AU", { maximumFractionDigits: 0 });
const fmtPct = (v) => (v == null ? "N/A (no price)" : Number(v).toFixed(2) + "%");
const fmtNum = (v) => (v == null || v === "" ? "—" : v);
const fmtDate = (v) => {
  if (!v) return "";
  try {
    return new Date(v).toLocaleString("en-AU");
  } catch (e) {
    return String(v);
  }
};

const STATUS_TABS = [
  { id: "all", label: "All" },
  { id: "pending", label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
];

const STATUS_BADGE = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-gray-200 text-gray-600",
};

const StatusBadge = ({ status }) => (
  <span
    className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${
      STATUS_BADGE[status] || "bg-gray-100 text-gray-600"
    }`}
  >
    {status}
  </span>
);

const UNIT_LABELS = {
  label: "Unit",
  beds: "Beds",
  baths: "Baths",
  size: "Size (sqm)",
  cars: "Car spaces",
  ownLaundry: "Own laundry",
  balcony: "Balcony / courtyard",
  status: "Status",
  rent: "Rent ($/wk)",
  leaseType: "Lease type",
  leaseEnd: "Lease end",
  bond: "Bond ($)",
  lastIncrease: "Last rent increase",
};

const AppraisalSubmissionsPage = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [acting, setActing] = useState(null); // "approve" | "reject" | null
  // On mobile we toggle between the list and the detail pane.
  const [mobileShowDetail, setMobileShowDetail] = useState(false);

  const fetchSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAppraisalSubmissions({
        search: searchTerm || undefined,
        status: statusFilter === "all" ? undefined : statusFilter,
      });
      const results = response.data?.results ?? [];
      const list = Array.isArray(results) ? results : [];
      setSubmissions(list);
      setSelectedId((prev) =>
        prev && list.some((s) => s.id === prev) ? prev : list[0]?.id ?? null
      );
    } catch (error) {
      console.error("Error fetching appraisal submissions:", error);
      if (error?.response?.status === 403) {
        toast.error("You don't have permission to view appraisal submissions.");
      } else {
        toast.error("Failed to load appraisal submissions");
      }
      setSubmissions([]);
      setSelectedId(null);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter]);

  // Debounce so we don't fire a request on every keystroke / tab flip.
  useEffect(() => {
    const t = setTimeout(fetchSubmissions, 300);
    return () => clearTimeout(t);
  }, [fetchSubmissions]);

  const selected = useMemo(
    () => submissions.find((s) => s.id === selectedId) || null,
    [submissions, selectedId]
  );

  const pendingCount = useMemo(
    () => submissions.filter((s) => s.status === "pending").length,
    [submissions]
  );

  const handleSelect = (id) => {
    setSelectedId(id);
    setMobileShowDetail(true);
  };

  // Replace a submission in the list with the server's updated copy.
  const applyUpdate = (updated) => {
    setSubmissions((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
  };

  const handleApprove = async () => {
    if (!selected) return;
    if (
      !window.confirm(
        "Approve this submission? This creates a CRM contact and a property at the Appraisal stage."
      )
    )
      return;
    try {
      setActing("approve");
      const { data } = await approveAppraisalSubmission(selected.id);
      applyUpdate(data);
      toast.success("Approved — CRM records created.");
    } catch (error) {
      console.error("Approve failed:", error);
      toast.error(error?.response?.data?.detail || "Failed to approve submission");
    } finally {
      setActing(null);
    }
  };

  const handleReject = async () => {
    if (!selected) return;
    const reason = window.prompt(
      "Reject this submission? Optionally add a reason (it won't be written to the CRM):",
      ""
    );
    // prompt returns null when cancelled.
    if (reason === null) return;
    try {
      setActing("reject");
      const { data } = await rejectAppraisalSubmission(selected.id, reason);
      applyUpdate(data);
      toast.success("Submission rejected.");
    } catch (error) {
      console.error("Reject failed:", error);
      toast.error(error?.response?.data?.detail || "Failed to reject submission");
    } finally {
      setActing(null);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#55C5D0]/10 flex items-center justify-center">
            <ClipboardList className="w-5 h-5 text-[#55C5D0]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#374151]">Appraisal Submissions</h1>
            <p className="text-sm text-gray-500">
              Review vendor answers from the Block of Units form, then approve to create
              the CRM record.
            </p>
          </div>
        </div>
      </div>

      {/* Status tabs */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {STATUS_TABS.map((tab) => {
          const isActive = statusFilter === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setStatusFilter(tab.id)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all ${
                isActive
                  ? "bg-[#374151] text-white"
                  : "bg-white border border-gray-200 text-gray-600 hover:border-gray-300"
              }`}
            >
              {tab.label}
              {tab.id === "pending" && pendingCount > 0 && (
                <span
                  className={`ml-1.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] ${
                    isActive ? "bg-white/20 text-white" : "bg-amber-100 text-amber-700"
                  }`}
                >
                  {pendingCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6">
        {/* List pane */}
        <div className={`${mobileShowDetail ? "hidden lg:block" : "block"}`}>
          {/* Search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search address or vendor…"
              className="w-full pl-9 pr-3 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-[#55C5D0] focus:ring-2 focus:ring-[#55C5D0]/20"
            />
          </div>

          {loading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-20 rounded-xl bg-gray-100 animate-pulse" />
              ))}
            </div>
          ) : submissions.length === 0 ? (
            <div className="bg-white border border-gray-100 rounded-xl p-8 text-center">
              <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-500">No submissions</p>
              <p className="text-xs text-gray-400 mt-1">
                {statusFilter === "all"
                  ? "Vendor intake forms will appear here once submitted."
                  : `No ${statusFilter} submissions.`}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="text-xs font-bold text-gray-400 uppercase tracking-wider px-1 mb-1">
                {submissions.length} submission{submissions.length === 1 ? "" : "s"}
              </div>
              {submissions.map((s) => {
                const isActive = s.id === selectedId;
                return (
                  <button
                    key={s.id}
                    onClick={() => handleSelect(s.id)}
                    className={`w-full text-left p-4 rounded-xl border transition-all ${
                      isActive
                        ? "border-[#55C5D0] bg-[#55C5D0]/5 shadow-sm"
                        : "border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-semibold text-sm text-[#374151] truncate">
                        {s.address || "Address TBD"}
                      </div>
                      <StatusBadge status={s.status} />
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 truncate">
                      {s.owner_name || "Unknown vendor"}
                    </div>
                    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                      {s.units != null && (
                        <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                          {s.units} unit{Number(s.units) === 1 ? "" : "s"}
                        </span>
                      )}
                      {s.tenanted != null && (
                        <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                          {s.tenanted} tenanted
                        </span>
                      )}
                    </div>
                    {(s.submitted_at || s.created_at) && (
                      <div className="text-[11px] text-gray-400 mt-2">
                        {fmtDate(s.submitted_at || s.created_at)}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Detail pane — the form sits on a darkened canvas so the white
            document reads as a page rather than blending into the app. */}
        <div className={`${mobileShowDetail ? "block" : "hidden lg:block"}`}>
          {selected ? (
            <div className={`${DOC_CANVAS} rounded-2xl p-3 sm:p-5`}>
              <SubmissionDetail
                submission={selected}
                acting={acting}
                onBack={() => setMobileShowDetail(false)}
                onApprove={handleApprove}
                onReject={handleReject}
                onOpenProperty={() =>
                  selected.created_property_id &&
                  navigate(`/properties/${selected.created_property_id}`)
                }
              />
            </div>
          ) : (
            !loading && (
              <div className={`${DOC_CANVAS} rounded-2xl p-3 sm:p-5`}>
                <div className={`${DOC_PAGE} rounded-xl p-12 text-center`}>
                  <ClipboardList className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                  <p className="text-sm text-gray-400">
                    Select a submission to view the answers.
                  </p>
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

const SubmissionDetail = ({
  submission,
  acting,
  onBack,
  onApprove,
  onReject,
  onOpenProperty,
}) => {
  const data = submission.appraisal_intake_data || {};
  const m = data.metrics || {};
  const answers = data.answers || {};
  const block = answers.block || {};
  const units = answers.units || [];
  const docs = answers.documents || data.documents || [];
  const submittedAt = data.submittedAt || submission.submitted_at || submission.created_at;
  const isPending = submission.status === "pending";
  const isBusy = acting !== null;

  const metricCells = [
    { l: "Units", v: fmtNum(m.units) },
    { l: "Bedrooms (total)", v: fmtNum(m.bedrooms) },
    { l: "Car spaces (total)", v: fmtNum(m.carSpaces) },
    { l: "Tenanted", v: fmtNum(m.tenanted) },
    { l: "Vacant", v: fmtNum(m.vacant) },
    { l: "Owner occupied", v: fmtNum(m.ownerOccupied) },
    { l: "Rent / week", v: fmtMoney(m.rentPerWeek) },
    { l: "Gross rent p.a.", v: fmtMoney(m.grossRentPerAnnum) },
    { l: "Total outgoings p.a.", v: fmtMoney(m.totalOutgoings) },
    { l: "Net rent p.a.", v: fmtMoney(m.netRentPerAnnum) },
    { l: "Price (provided)", v: m.price == null ? "—" : fmtMoney(m.price) },
    { l: "Gross yield", v: fmtPct(m.grossYieldPct) },
    { l: "Net yield", v: fmtPct(m.netYieldPct) },
  ];

  const blockEntries = Object.entries(block)
    .map(([k, v]) => ({ k, question: (v && v.question) || k, answer: v && v.answer }))
    .filter((e) => e.answer != null && String(e.answer).trim() !== "");

  return (
    <div className="space-y-6">
      {/* Header card */}
      <div className={`${DOC_PAGE} p-6 rounded-xl`}>
        {/* Mobile back button */}
        <button
          onClick={onBack}
          className="lg:hidden flex items-center gap-1.5 text-sm font-medium text-gray-500 mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> Back to list
        </button>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-bold text-[#374151]">
                {submission.address || "Address TBD"}
              </h3>
              <StatusBadge status={submission.status} />
            </div>
            <p className="text-sm text-gray-500 mt-0.5">
              {submission.owner_name || "Unknown vendor"}
              {submission.owner_contact ? ` · ${submission.owner_contact}` : ""}
              {submission.owner_email ? ` · ${submission.owner_email}` : ""}
            </p>
            {submittedAt && (
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mt-2">
                Submitted {fmtDate(submittedAt)}
              </p>
            )}
            {submission.status === "approved" && submission.reviewed_by && (
              <p className="text-xs text-green-600 mt-1">
                Approved by {submission.reviewed_by}
                {submission.reviewed_at ? ` · ${fmtDate(submission.reviewed_at)}` : ""}
              </p>
            )}
            {submission.status === "rejected" && (
              <p className="text-xs text-gray-500 mt-1">
                Rejected{submission.reviewed_by ? ` by ${submission.reviewed_by}` : ""}
                {submission.rejection_reason ? ` · "${submission.rejection_reason}"` : ""}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {isPending ? (
              <>
                <button
                  onClick={onReject}
                  disabled={isBusy}
                  className="flex items-center gap-1.5 px-4 py-2 border border-red-200 text-red-600 hover:bg-red-50 rounded-lg text-xs font-bold transition-all disabled:opacity-50"
                >
                  {acting === "reject" ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <X className="w-3.5 h-3.5" />
                  )}
                  Reject
                </button>
                <button
                  onClick={onApprove}
                  disabled={isBusy}
                  className="flex items-center gap-1.5 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-bold transition-all disabled:opacity-50"
                >
                  {acting === "approve" ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Check className="w-3.5 h-3.5" />
                  )}
                  Approve
                </button>
              </>
            ) : (
              submission.created_property_id && (
                <button
                  onClick={onOpenProperty}
                  className="flex items-center gap-1.5 px-4 py-2 bg-[#374151] hover:bg-[#1F2937] text-white rounded-lg text-xs font-bold transition-all whitespace-nowrap"
                >
                  View full property <ExternalLink className="w-3.5 h-3.5" />
                </button>
              )
            )}
          </div>
        </div>

        {isPending && (
          <div className="mt-4 text-xs text-gray-500 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
            This submission has not been written to the CRM yet. Approving creates the
            vendor contact and a property at the Appraisal stage.
          </div>
        )}
      </div>

      {/* Key numbers */}
      <div className={`${DOC_PAGE} p-6 rounded-xl`}>
        <h4 className="text-xs font-black uppercase tracking-[0.15em] text-[#374151] opacity-60 mb-4">
          Key Numbers
        </h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
          {metricCells.map((c) => (
            <div key={c.l} className="border border-gray-100 rounded-lg p-4 bg-gray-50/50">
              <div className="text-xl font-black text-[#374151] tabular-nums">{c.v}</div>
              <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wide mt-1">
                {c.l}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Units */}
      {units.length > 0 && (
        <div className={`${DOC_PAGE} p-6 rounded-xl`}>
          <h4 className="text-xs font-black uppercase tracking-[0.15em] text-[#374151] opacity-60 mb-4">
            Units ({units.length})
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {units.map((u, i) => (
              <div key={i} className="border border-gray-100 rounded-lg p-4">
                <div className="font-bold text-[#374151] mb-2">{u.label || "Unit " + (i + 1)}</div>
                <dl className="space-y-1">
                  {Object.keys(UNIT_LABELS).map((key) => {
                    if (key === "label") return null;
                    const val = u[key];
                    if (val == null || String(val).trim() === "") return null;
                    return (
                      <div key={key} className="flex justify-between gap-4 text-sm">
                        <dt className="text-gray-500">{UNIT_LABELS[key]}</dt>
                        <dd className="text-[#374151] font-medium text-right">{String(val)}</dd>
                      </div>
                    );
                  })}
                </dl>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All answers */}
      <div className={`${DOC_PAGE} p-6 rounded-xl`}>
        <h4 className="text-xs font-black uppercase tracking-[0.15em] text-[#374151] opacity-60 mb-4">
          All Answers
        </h4>
        {blockEntries.length === 0 ? (
          <p className="text-sm text-gray-400">No answers recorded.</p>
        ) : (
          <dl className="divide-y divide-gray-100">
            {blockEntries.map((e) => (
              <div key={e.k} className="py-3 grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4">
                <dt className="text-sm text-gray-500 sm:col-span-1">{e.question}</dt>
                <dd className="text-sm text-[#374151] font-medium sm:col-span-2 whitespace-pre-wrap">
                  {String(e.answer)}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>

      {/* Documents the owner can provide */}
      {docs.length > 0 && (
        <div className={`${DOC_PAGE} p-6 rounded-xl`}>
          <h4 className="text-xs font-black uppercase tracking-[0.15em] text-[#374151] opacity-60 mb-4">
            Documents The Owner Can Provide
          </h4>
          <ul className="space-y-2">
            {docs.map((d, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[#374151]">
                <span className="text-[#55C5D0] font-black">✓</span>
                <span>{d}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AppraisalSubmissionsPage;
